import { Trans } from "@lingui/react/macro";
import { Button, Input, type InputProps } from "@offstreetparking/design-system/forms";
import { useDebounce } from "@offstreetparking/design-system/hooks";
import { Text } from "@offstreetparking/design-system/typography";
import { useEffect, useRef, useState } from "react";
import { offstreetClient } from "../../../api/client";
import { suggestEmail } from "./suggest-email";

type EmailInputProps = Omit<InputProps, "onChange"> & {
	onChange: (email: string) => void;
	companyId: number;
};

export const EmailInput = (props: EmailInputProps) => {
	const [emailValue, setEmailValue] = useState(props.value);
	const debouncedEmail = useDebounce(emailValue, 500);
	const [emailTypoFixerState, setEmailTypoFixerState] = useState<ReturnType<typeof suggestEmail>>();
	const ref = useRef<HTMLInputElement>(null);
	const { companyId, ...rest } = props;
	const suggestion = emailTypoFixerState?.suggested;

	const { data: knownDomains = [] } = offstreetClient.companies.getKnownEmailDomains.useQuery(
		{
			companyId: companyId,
		},
		{
			staleTime: 24 * 60 * 60 * 1000,
		},
	);
	useEffect(() => {
		const email = (debouncedEmail?.toString() ?? "").trim();
		if (email) {
			setEmailTypoFixerState(suggestEmail(email.toLocaleLowerCase(), knownDomains));
		}
	}, [debouncedEmail, knownDomains]);
	return (
		<>
			<Input
				ref={ref}
				{...rest}
				autoComplete="email"
				onChange={(e) => {
					setEmailValue(e.target.value);
					props.onChange?.(e.target.value.toLocaleLowerCase());
				}}
				onClear={props.onClear ? () => props?.onClear?.() : undefined}
			/>
			{emailTypoFixerState?.hasCorrection && (
				<Text size="xs" intent={"muted"} className="mt-2">
					<Trans>
						Did you mean{" "}
						<Button
							type="button"
							size="xs"
							intent={"secondary"}
							onClick={() => {
								if (ref.current) {
									// @ts-expect-error - this should be fixed in next version of email-typo-fixer
									ref.current.value = suggestion;
									setEmailValue(suggestion);
									setEmailTypoFixerState(undefined);
									if (suggestion) {
										props.onChange?.(suggestion);
									}
								}
							}}
						>
							{suggestion}
						</Button>
						?
					</Trans>
				</Text>
			)}
		</>
	);
};
